/* eslint-disable no-console */
import {Component, Mixins, Watch} from 'vue-property-decorator';
import {MetaMixin} from '../../mixins/meta.mixin';
import {RouteName} from '../routes.constants';
import {magic} from '@gig/magic-sdk';
import {
    USER_LOGGED_IN_QUERY,
    WINDOW_ROUTE_CHANGE,
    WINDOW_BEFORE_UNLOAD,
    USER_LOGGED_IN_UPDATED
} from '@gig/magic-messages';
import type {ISubscription} from '@gig/magic-message-bus/dist/base-bus';
import {getFeatureValueV2} from '@gig/magic-common';


enum PostMethods {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    PAGE_NAVIGATION = 'PAGE_NAVIGATION',
}

interface IPostMethodsArguments {
    url?: string;
    sessionId?: number;
    targetPage?: string;
}

export const JACKPOTS_READY = 'JACKPOTS_READY';
export const JACKPOTS = 'jackpots';

@Component({})
export default class JackpotsRoute extends Mixins(MetaMixin) {
    url: string = '';
    isIframeReady: boolean = false;
    iframeHeight: string = '';
    iframeOrigin: string = '';

    private sessionId = '';
    private userId: string | number | undefined;
    private routeChangeSubscription: ISubscription | undefined;
    private userLoggedInSubscription: ISubscription | null = null;
    private windowUnloadSubscription: ISubscription | undefined;

    get iframeStyle() {
        return {height: this.iframeHeight};
    }

    async created() {
        // For the case after redirected to login
        const redirectUrl = sessionStorage.getItem('redirectUrl')
        sessionStorage.removeItem('redirectUrl');
        redirectUrl && this.$router.push(redirectUrl);

        this.url = await getFeatureValueV2(JACKPOTS);
        this.iframeOrigin = this.url;

        window.addEventListener('message', this.handleIframeMessage);

        const metaUrl = this.getMetaUrl(this.$route.params?.jackpots);
        this.setPage(`jackpots`, {slug: metaUrl});
    }

    destroyed() {
        window.removeEventListener('message', this.handleIframeMessage);
        this.routeChangeSubscription?.unsubscribe();
        this.windowUnloadSubscription?.unsubscribe();
        this.userLoggedInSubscription?.unsubscribe();
    }

    private async handleIframeMessage(event: MessageEvent) {
        if (!this.isJackpotEvent(event)) {
            console.warn(
                'Origin mismatch or unauthorized source:',
                event.origin
            );
            return;
        }

        console.log('jackpots event data: ', event.data, event);

        const data = event.data;
        if (data.action === 'IFRAME_LOADED') {
            this.isIframeReady = true;
            const currentUrl = window.location.pathname;

            /*
                relativePath for '/jackpots/winners' = winners
                relativePath for '/jackpots/' = ''
                trailing '/' can be removed or added after jackpots
            */
            const relativePath = this.$route.params?.jackpots ?? ''

            if (relativePath.length > 0) {
                this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                    targetPage: currentUrl.replace(
                        '/jackpots/',
                        ''
                    ),
                });
            } else {
                this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                    targetPage: '/',
                });
            }

            if (data.additionalData?.height) {
                const height = data.additionalData.height;
                this.iframeHeight = `${height}px`;
                this.windowUnloadSubscription = window.eventBus.subscribe(
                    WINDOW_BEFORE_UNLOAD,
                    () => {
                        this.iframeLogout();
                    }
                );
                this.routeChangeSubscription = window.eventBus.subscribe<
                    string
                >(WINDOW_ROUTE_CHANGE, (event) => {
                    if (!event.payload!.match('/jackpots')) {
                        this.iframeLogout();
                    }
                });
                this.userLoggedInSubscription = window.eventBus.subscribe<
                    boolean
                >(
                    USER_LOGGED_IN_UPDATED,
                    (event: any) => {
                        if (event.payload === false) {
                            this.iframeLogout();
                        }
                    },
                    {replay: true}
                );

                window.eventBus.publish(JACKPOTS_READY, true);
                await this.iframeLogin();
            }
        } else if (
            data.action === 'IFRAME_RESIZE' &&
            data.additionalData?.height
        ) {
            const height = data.additionalData.height;
            this.iframeHeight = `${height}px`;
        } else if (
            data.action === 'PAGE_NAVIGATION' &&
            data.additionalData.targetPage
        ) {
            const targetPage = data.additionalData.targetPage;
            const height = data.additionalData.height;

            this.iframeHeight = `${height}px`;

            const currentUrl = window.location.pathname;
            const targetPath = `/jackpots${targetPage}`;

            if (currentUrl !== targetPath) {
                this.$router.replace(targetPath);
            }

            window.eventBus.publish(WINDOW_ROUTE_CHANGE, targetPath);

            const metaUrl = this.getMetaUrl(targetPage.substring(1))
            this.setPage(`jackpots`, {slug: metaUrl});

        } else if (data.action === 'REDIRECTION' && data.additionalData.feature) {
            const feature = data.additionalData.feature;
            const redirectUrl = data.additionalData.redirectUrl
            sessionStorage.setItem('redirectUrl', `${redirectUrl}`);

            if (await this.isLoggedIn()) {
                return;
            }
            if (feature === 'registration') {
                this.$router.push({
                    name: RouteName.SIGN_UP,
                    replace: true
                });
            } else if (feature === 'login') {
                this.$router.push({
                    name: RouteName.LOGIN,
                    query: {
                        backTo: redirectUrl,
                    },
                });
            }
        }
    }

    private isJackpotEvent(event: MessageEvent) {
        return event.origin === this.iframeOrigin;
    }

    private getMetaUrl(location: string): string {
        if (location) {
            return location.replaceAll('/', '.')
        }
        return ``;
    }

    private isLoggedIn() {
        return window.queryBus.query<boolean>(USER_LOGGED_IN_QUERY);
    }

    private async iframeLogin() {
        if ((await this.isLoggedIn()) && this.isIframeReady) {
            this.sessionId = await magic.methods.externalServices.getSessionHash();
            const user = magic.session.getUser();
            this.userId = user?.userId;
            this.postMessageMethod(PostMethods.LOGIN);
        }
    }

    private iframeLogout() {
        if (!this.sessionId) {
            return;
        }

        this.postMessageMethod(PostMethods.LOGOUT);
    }

    private postMessageMethod(
        method: PostMethods,
        args?: IPostMethodsArguments
    ): void {
        const json = {
            action: method,
            additionalData: {
                playerId: this.userId,
                sessionHash: this.sessionId,
                ...args,
            },
        };

        const jackpots = this.$refs.jackpot as HTMLIFrameElement;
        jackpots.contentWindow!.postMessage(json, this.iframeOrigin);
    }
    
    @Watch('$route.fullPath')
    onRouteChanged() {
        const jackpotsSlug = this.$route.params?.jackpots;
    
        const metaUrl = this.getMetaUrl(jackpotsSlug || '');
        this.setPage('jackpots', { slug: metaUrl });
    
        this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
            targetPage: jackpotsSlug ? jackpotsSlug : '/',
        });
    }    
}


